/**
 * Pure decision helper for the 30s cloud auto-sync tick.
 *
 * Extracted from shell.js so the gate is testable without booting the shell.
 * Every reason the tick may skip a save lives here; shell.js owns the flags and
 * the side effects.
 */

/**
 * @param {object} input
 * @param {boolean} [input.paused] auth 401 pause (see shell's pauseCloudSync)
 * @param {boolean} [input.conflictPending] a 409 is awaiting user resolution
 * @param {boolean} [input.agentRunActive] the AI agent owns the version chain right now
 * @param {number} [input.updatedAt] live state.updatedAt
 * @param {number} [input.lastSyncedAt] the updatedAt the last save actually uploaded
 * @returns {string|null} the skip reason, or null when the tick should save
 */
export function cloudSyncSkipReason({
  paused = false,
  conflictPending = false,
  agentRunActive = false,
  updatedAt = 0,
  lastSyncedAt = 0
} = {}) {
  if (paused) return 'paused';
  if (conflictPending) return 'conflict';
  // The agent holds an optimistic-concurrency base for the whole turn and
  // validates it only at save time. A tick landing mid-run advances
  // latest_version underneath it and guarantees a 409.
  if (agentRunActive) return 'agent-run';
  const live = Number(updatedAt) || 0;
  if (!live) return 'no-state';
  // Strictly greater: an edit that landed while a previous upload was in flight
  // shares no timestamp with it, so `<=` is the correct "already synced" test.
  if (live <= (Number(lastSyncedAt) || 0)) return 'clean';
  return null;
}

/** Convenience wrapper — true when the tick should not save. */
export function shouldSkipCloudSync(input) {
  return cloudSyncSkipReason(input) !== null;
}
